(function () {
// Sliders are set up just before they come into view instead of all at once on page load,
// and autoplay is paused while a slider is far from the viewport. Every Swiper's options are unchanged.
//
// - Sliders inside the viewport when the page loads are initialized straight away (same as before).
// - The rest start when they get within SG_SLIDER_NEAR_PX of the viewport, one per task, so the
//   work never blocks the main thread in one long chunk and is done before the slider is seen.
// - After the first scroll/tap/key the remaining sliders are also initialized in idle time.
// - Without IntersectionObserver everything initializes on page load, as it always did.
var SG_SLIDER_NEAR_PX = 1500;
// Autoplay keeps running within this distance of the viewport (wider than SG_SLIDER_NEAR_PX, so a slider set up on
// approach starts autoplaying at once) and is already mid-cycle by the time the visitor reaches it
var SG_AUTOPLAY_NEAR_PX = 2000;
var sgHasIO = 'IntersectionObserver' in window;
var sgPending = new Map();   // container -> its init function, until it has run
var sgCandidates = [];       // containers registered during DOMContentLoaded, settled at its end
var sgQueue = [];            // near-viewport containers waiting for their own task
var sgPumping = false;
var sgAutoplayState = typeof WeakMap === 'function' ? new WeakMap() : new Map();

var sgNearObserver = sgHasIO ? new IntersectionObserver(sgOnNear, { rootMargin: SG_SLIDER_NEAR_PX + 'px 0px' }) : null;
var sgViewObserver = sgHasIO ? new IntersectionObserver(sgOnView, { rootMargin: SG_AUTOPLAY_NEAR_PX + 'px 0px' }) : null;

function sgIsLive(container) {
  return !!(container.swiper && !container.swiper.destroyed);
}

function sgInViewport(rect) {
  var vh = window.innerHeight || document.documentElement.clientHeight;
  return (rect.width > 0 || rect.height > 0) && rect.bottom > 0 && rect.top < vh;
}

function sgRun(container) {
  var init = sgPending.get(container);
  if (!init) return;
  sgPending.delete(container);
  if (sgNearObserver) sgNearObserver.unobserve(container);
  if (!container.isConnected || sgIsLive(container)) return;
  init(container);
}

function sgPump() {
  if (sgPumping || !sgQueue.length) return;
  sgPumping = true;
  setTimeout(function step() {
    var container = sgQueue.shift();
    if (container) sgRun(container);
    if (sgQueue.length) {
      setTimeout(step, 0);
    } else {
      sgPumping = false;
    }
  }, 0);
}

function sgOnNear(entries) {
  entries.forEach(function (entry) {
    var container = entry.target;
    if (!entry.isIntersecting || !sgPending.has(container)) return;
    if (sgInViewport(entry.boundingClientRect)) {
      sgRun(container);
    } else if (sgQueue.indexOf(container) === -1) {
      sgQueue.push(container);
    }
  });
  sgPump();
}

// Register a slider container to be initialized lazily with init(container)
function sgLazyInit(container, init) {
  if (!sgHasIO) {
    init(container);
    return;
  }
  sgPending.set(container, init);
  sgCandidates.push(container);
}

// Forget a pending lazy init (the container is being initialized or torn down by other code)
function sgCancelLazyInit(container) {
  sgPending.delete(container);
  if (sgNearObserver) sgNearObserver.unobserve(container);
  var i = sgQueue.indexOf(container);
  if (i !== -1) sgQueue.splice(i, 1);
}

// Runs once at the end of DOMContentLoaded: sliders already on screen initialize now, the rest are observed.
// Positions are read in one batch per pass; a second pass only happens if an init above moved things.
function sgSettle() {
  var rest = sgCandidates.filter(function (c) { return sgPending.has(c); });
  sgCandidates = [];
  var changed = true;
  while (changed && rest.length) {
    changed = false;
    var rects = rest.map(function (c) { return c.getBoundingClientRect(); });
    var left = [];
    rest.forEach(function (c, i) {
      if (sgInViewport(rects[i])) {
        sgRun(c);
        changed = true;
      } else {
        left.push(c);
      }
    });
    rest = left;
  }
  rest.forEach(function (c) {
    if (sgPending.has(c)) sgNearObserver.observe(c);
  });
  if (sgPending.size) {
    SG_WARM_EVENTS.forEach(function (type) {
      window.addEventListener(type, sgStartWarmup, { passive: true });
    });
  }
}

// Once the visitor starts interacting, the remaining sliders are initialized in idle time, one per
// idle period, so the page settles into its final layout (and scrollbar size) early, even when the
// visitor jumps far down the page (scrollbar drag, End key, restored scroll position).
var SG_WARM_EVENTS = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'];
var sgWarmStarted = false;

function sgStartWarmup() {
  if (sgWarmStarted) return;
  sgWarmStarted = true;
  SG_WARM_EVENTS.forEach(function (type) {
    window.removeEventListener(type, sgStartWarmup, { passive: true });
  });
  var idle = window.requestIdleCallback
    ? function (cb) { window.requestIdleCallback(cb, { timeout: 1000 }); }
    : function (cb) { setTimeout(cb, 50); };
  idle(function next() {
    var it = sgPending.keys().next();
    if (it.done) return;
    sgRun(it.value);
    if (sgPending.size) idle(next);
  });
}

// Same hover behaviour as before (stop autoplay on mouseenter, start it on mouseleave), plus:
// a running autoplay is stopped while the slider is more than SG_AUTOPLAY_NEAR_PX from the viewport and started again when it is back.
function sgBindAutoplayHover(container, swiper) {
  var state = { swiper: swiper, hovered: false, visible: true, parked: false };
  sgAutoplayState.set(container, state);

  container.addEventListener('mouseenter', function () {
      if (swiper.destroyed) return;
      state.hovered = true;
      state.parked = false;
      swiper.autoplay.stop();  // Stop autoplay on hover
  });

  container.addEventListener('mouseleave', function () {
      if (swiper.destroyed) return;
      state.hovered = false;
      if (!state.visible) {
          state.parked = true;  // resume once it is back on screen
          return;
      }
      swiper.autoplay.start();  // Resume autoplay after hover
  });

  if (sgViewObserver) {
      sgViewObserver.unobserve(container);
      sgViewObserver.observe(container);
  }
}

function sgOnView(entries) {
  entries.forEach(function (entry) {
    var state = sgAutoplayState.get(entry.target);
    if (!state || state.swiper.destroyed || !state.swiper.autoplay) return;
    state.visible = entry.isIntersecting;
    if (!state.visible) {
      if (state.swiper.autoplay.running) {
        state.swiper.autoplay.stop();
        state.parked = true;
      }
    } else if (state.parked) {
      state.parked = false;
      if (!state.hovered) state.swiper.autoplay.start();
    }
  });
}

document.addEventListener('DOMContentLoaded', function () {
  var herosliderSwipers = [];

  function initherosliderSwiper(swiperContainer) {
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;
      var slider = swiperContainer.dataset.slider;
    
      var herosliderSwiper = new Swiper(swiperContainer, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          navigation: {
              nextEl: swiperContainer.querySelector(".slider-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".slider-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".slider-swiper-pagination"),
              clickable: true,
          },
      });

      // Hover par autoplay stop and start functionality
      sgBindAutoplayHover(swiperContainer, herosliderSwiper);

      herosliderSwipers.push(herosliderSwiper);
  }

  // Initialize Hero slider Swiper on page load
  document.querySelectorAll('.classic-container').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initherosliderSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Hero slider Swiper on section select
      herosliderSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      herosliderSwipers = [];
      document.querySelectorAll('.classic-container').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initherosliderSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Hero slider Swiper on section deselect if necessary
      herosliderSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      herosliderSwipers = [];
      document.querySelectorAll('.classic-container').forEach(sgCancelLazyInit);
  });
});

// featured collection slider js

document.addEventListener('DOMContentLoaded', function () {
  var FeaturedcollectionSwipers = [];

  function initFeaturedcollectionSwiper(swiperContainer) {
      var enable = swiperContainer.dataset.enable;
      var desktopspace = swiperContainer.dataset.desktopspace;
      var mobilespace = swiperContainer.dataset.mobilespace;
      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;

    if (enable) {
      var FeaturedcollectionSwiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: desktopspace,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".featured-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".featured-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".featured-swiper-pagination"),
              clickable: true,
          },
          breakpoints: {
            320: {
                slidesPerView: mobile,
                spaceBetween: mobilespace
            },
            576: {
                slidesPerView: tablet,
                spaceBetween: mobilespace
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: mobilespace
            },
            992: {
                slidesPerView: desktop,
                spaceBetween: desktopspace
            },
        },
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, FeaturedcollectionSwiper);

      FeaturedcollectionSwipers.push(FeaturedcollectionSwiper);
    }
  }

  // Initialize Featured collection Swiper on page load
  document.querySelectorAll('.featured-slider').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initFeaturedcollectionSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Featured collection Swiper on section select
      FeaturedcollectionSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      FeaturedcollectionSwipers = [];
      document.querySelectorAll('.featured-slider').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initFeaturedcollectionSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Featured collection Swiper on section deselect if necessary
      FeaturedcollectionSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      FeaturedcollectionSwipers = [];
      document.querySelectorAll('.featured-slider').forEach(sgCancelLazyInit);
  });
});

// tab collection slider js

document.addEventListener('DOMContentLoaded', function () {
  var TabcollectionSwipers = [];

  function initTabcollectionSwiper(swiperContainer) {
      var enable = swiperContainer.dataset.enable;
      var desktopspace = swiperContainer.dataset.desktopspace;
      var mobilespace = swiperContainer.dataset.mobilespace;
      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;

    if (enable) {
      var TabcollectionSwiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: desktopspace,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".tab-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".tab-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".tab-swiper-pagination"),
              clickable: true,
          },
          breakpoints: {
            320: {
                slidesPerView: mobile,
                spaceBetween: mobilespace
            },
            576: {
                slidesPerView: tablet,
                spaceBetween: mobilespace
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: mobilespace
            },
            992: {
                slidesPerView: desktop,
                spaceBetween: desktopspace
            },
        },
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, TabcollectionSwiper);

      TabcollectionSwipers.push(TabcollectionSwiper);
    }
  }

  // Tabs rendered later (deferred tab panels in tabs-collection) initialize through this
  // and must be ready at once, so this entry point always initializes immediately
  window.initTabcollectionSwiper = function (swiperContainer) {
      sgCancelLazyInit(swiperContainer);
      if (sgIsLive(swiperContainer)) return;
      initTabcollectionSwiper(swiperContainer);
  };

  // Initialize Tab collection Swiper on page load
  document.querySelectorAll('.tab-slider').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initTabcollectionSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Tab collection Swiper on section select
      TabcollectionSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      TabcollectionSwipers = [];
      document.querySelectorAll('.tab-slider').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initTabcollectionSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Tab collection Swiper on section deselect if necessary
      TabcollectionSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      TabcollectionSwipers = [];
      document.querySelectorAll('.tab-slider').forEach(sgCancelLazyInit);
  });
});

// blog slider js

document.addEventListener('DOMContentLoaded', function () {
  var FeaturedblogSwipers = [];

  function initFeaturedblogSwiper(swiperContainer) {
      var desktopspace = swiperContainer.dataset.desktopspace;
      var mobilespace = swiperContainer.dataset.mobilespace;
      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;
  
      var FeaturedblogSwiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: desktopspace,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".blog-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".blog-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".blog-swiper-pagination"),
              clickable: true,
          },
          breakpoints: {
            320: {
                slidesPerView: mobile,
                spaceBetween: mobilespace
            },
            576: {
                slidesPerView: tablet,
                spaceBetween: mobilespace
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: mobilespace
            },
            992: {
                slidesPerView: desktop,
                spaceBetween: desktopspace
            },
        },
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, FeaturedblogSwiper);

      FeaturedblogSwipers.push(FeaturedblogSwiper);
  }

  // Initialize Featured blog Swiper on page load
  document.querySelectorAll('.blog-slider').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initFeaturedblogSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Featured blog Swiper on section select
      FeaturedblogSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      FeaturedblogSwipers = [];
      document.querySelectorAll('.blog-slider').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initFeaturedblogSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Featured blog Swiper on section deselect if necessary
      FeaturedblogSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      FeaturedblogSwipers = [];
      document.querySelectorAll('.blog-slider').forEach(sgCancelLazyInit);
  });
});

// category collection 1 slider

document.addEventListener('DOMContentLoaded', function () {
  var categorycollection1Swipers = [];

  function initcategorycollection1Swiper(swiperContainer) {

      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;
      var desktopspace = swiperContainer.dataset.desktopspace;
      var mobilespace = swiperContainer.dataset.mobilespace;

      var categorycollection1Swiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: desktopspace,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".category1-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".category1-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".category1-swiper-pagination"),
              clickable: true,
          },
          breakpoints : {
            320: {
                slidesPerView: mobile,
                spaceBetween: mobilespace,
            },
            480: {
                slidesPerView: tablet,
                spaceBetween: mobilespace,
            },
            576: {
                slidesPerView: tablet,
                spaceBetween: mobilespace,
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: mobilespace,
            },
            992: {
                slidesPerView: desktop,
                spaceBetween: desktopspace,
            },
            1200: {
                slidesPerView: desktop,
                spaceBetween: desktopspace,
            },
          }
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, categorycollection1Swiper);

      categorycollection1Swipers.push(categorycollection1Swiper);
  }

  // Initialize category collection Swiper on page load
  document.querySelectorAll('.main-collections-list-wrapper1').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initcategorycollection1Swiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize category collection Swiper on section select
      categorycollection1Swipers.forEach(function (swiper) {
          swiper.destroy();
      });
      categorycollection1Swipers = [];
      document.querySelectorAll('.main-collections-list-wrapper1').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initcategorycollection1Swiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup category collection Swiper on section deselect if necessary
      categorycollection1Swipers.forEach(function (swiper) {
          swiper.destroy();
      });
      categorycollection1Swipers = [];
      document.querySelectorAll('.main-collections-list-wrapper1').forEach(sgCancelLazyInit);
  });
});

// category collection 3 slider

document.addEventListener('DOMContentLoaded', function () {
  var categorycollection3Swipers = [];

  function initcategorycollection3Swiper(swiperContainer) {

      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;
      var desktopspace = swiperContainer.dataset.desktopspace;
      var mobilespace = swiperContainer.dataset.mobilespace;

      var categorycollection3Swiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: desktopspace,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".category3-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".category3-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".category3-swiper-pagination"),
              clickable: true,
          },
          breakpoints : {
            320: {
                slidesPerView: mobile,
                spaceBetween: mobilespace,
            },
            480: {
                slidesPerView: tablet,
                spaceBetween: mobilespace,
            },
            576: {
                slidesPerView: tablet,
                spaceBetween: mobilespace,
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: mobilespace,
            },
            992: {
                slidesPerView: desktop,
                spaceBetween: desktopspace,
            },
            1200: {
                slidesPerView: desktop,
                spaceBetween: desktopspace,
            },
          }
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, categorycollection3Swiper);

      categorycollection3Swipers.push(categorycollection3Swiper);
  }

  // Initialize category collection Swiper on page load
  document.querySelectorAll('.main-collections-list-wrapper3').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initcategorycollection3Swiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize category collection Swiper on section select
      categorycollection3Swipers.forEach(function (swiper) {
          swiper.destroy();
      });
      categorycollection3Swipers = [];
      document.querySelectorAll('.main-collections-list-wrapper3').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initcategorycollection3Swiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup category collection Swiper on section deselect if necessary
      categorycollection3Swipers.forEach(function (swiper) {
          swiper.destroy();
      });
      categorycollection3Swipers = [];
      document.querySelectorAll('.main-collections-list-wrapper3').forEach(sgCancelLazyInit);
  });
});

// multicolumn slider
document.addEventListener('DOMContentLoaded', function () {
  var MulticolumnSwipers = [];

  function initmulticolumnSwiper(swiperContainer) {
      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;

      var MulticolumnSwiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: 30,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".multicolumn-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".multicolumn-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".multicolumn-swiper-pagination"),
              clickable: true,
          },
          breakpoints: {
            320: {
                slidesPerView: mobile,
                spaceBetween: 15
            },
            576: {
                slidesPerView: tablet,
                spaceBetween: 15
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: 15
            },
            992: {
                slidesPerView: desktop,
                spaceBetween: 30
            },
          },
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, MulticolumnSwiper);

      MulticolumnSwipers.push(MulticolumnSwiper);
  }

  // Initialize Multicolumn Swiper on page load
  document.querySelectorAll('.multicolumn-slider').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initmulticolumnSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Multicolumn Swiper on section select
      MulticolumnSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      MulticolumnSwipers = [];
      document.querySelectorAll('.multicolumn-slider').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initmulticolumnSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Multicolumn Swiper on section deselect if necessary
      MulticolumnSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      MulticolumnSwipers = [];
      document.querySelectorAll('.multicolumn-slider').forEach(sgCancelLazyInit);
  });
});

// Best selling collection slider js

document.addEventListener('DOMContentLoaded', function () {
  var BestsellingcollectionSwipers = [];

  function initBestsellingcollectionSwiper(swiperContainer) {
      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;

      var BestsellingcollectionSwiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: 30,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".bestselling-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".bestselling-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".bestselling-swiper-pagination"),
              clickable: true,
          },
          breakpoints: {
            320: {
                slidesPerView: mobile,
                spaceBetween: 15
            },
            576: {
                slidesPerView: tablet,
                spaceBetween: 15
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: 15
            },
            992: {
                slidesPerView: desktop,
                spaceBetween: 30
            },
          },
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, BestsellingcollectionSwiper);

      BestsellingcollectionSwipers.push(BestsellingcollectionSwiper);
  }

  // Initialize Bestselling collection Swiper on page load
  document.querySelectorAll('.bestselling-slider').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initBestsellingcollectionSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Bestselling collection Swiper on section select
      BestsellingcollectionSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      BestsellingcollectionSwipers = [];
      document.querySelectorAll('.bestselling-slider').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initBestsellingcollectionSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Bestselling collection Swiper on section deselect if necessary
      BestsellingcollectionSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      BestsellingcollectionSwipers = [];
      document.querySelectorAll('.bestselling-slider').forEach(sgCancelLazyInit);
  });
});

// Video with text slider js

document.addEventListener('DOMContentLoaded', function () {
  var VideoproductSwipers = [];

  function initVideoproductSwiper(swiperContainer) {
      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;

      var VideoproductSwiper = new Swiper(swiperContainer, {
          slidesPerView: desktop,
          spaceBetween: 30,
          loop: loop,
          autoplay: autoplay,
          speed: speed,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".videoproduct-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".videoproduct-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".videoproduct-swiper-pagination"),
              clickable: true,
          },
          breakpoints: {
            320: {
                slidesPerView: mobile,
                spaceBetween: 15
            },
            480: {
                slidesPerView: tablet,
                spaceBetween: 15
            },
            768: {
                slidesPerView: largeTablet,
                spaceBetween: 15
            },
            992: {
                slidesPerView: largeTablet,
                spaceBetween: 30
            },
            1200: {
                slidesPerView: desktop,
                spaceBetween: 30
            }
        },
      });

      // Add hover functionality to stop and start autoplay
      sgBindAutoplayHover(swiperContainer, VideoproductSwiper);

      VideoproductSwipers.push(VideoproductSwiper);
  }

  // Initialize Videoproduct collection Swiper on page load
  document.querySelectorAll('.video-product-slider').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initVideoproductSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Videoproduct collection Swiper on section select
      VideoproductSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      VideoproductSwipers = [];
      document.querySelectorAll('.video-product-slider').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initVideoproductSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Videoproduct collection Swiper on section deselect if necessary
      VideoproductSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      VideoproductSwipers = [];
      document.querySelectorAll('.video-product-slider').forEach(sgCancelLazyInit);
  });
});

// Gallery slider js

document.addEventListener('DOMContentLoaded', function () {
  var GallerySwipers = [];

  function initGallerySwiper(swiperContainer) {
      var enable = swiperContainer.dataset.enable;
      var desktopspace = swiperContainer.dataset.desktopspace;
      var mobilespace = swiperContainer.dataset.mobilespace;
      var desktop = swiperContainer.dataset.desktop;
      var largeTablet = swiperContainer.dataset.largetablet;
      var tablet = swiperContainer.dataset.tablet;
      var mobile = swiperContainer.dataset.mobile;
      var autoplay = swiperContainer.dataset.autoplay === "true";
      var loop = swiperContainer.dataset.loop === "true";
      var speed = swiperContainer.dataset.speed;

      if (enable) {
          var GallerySwiper = new Swiper(swiperContainer, {
              slidesPerView: desktop,
              spaceBetween: desktopspace,
              loop: loop,
              autoplay: autoplay,
              speed: speed,
              grabCursor: true,
              a11y: false,
              navigation: {
                  nextEl: swiperContainer.querySelector(".gallery-swiper-button-next"),
                  prevEl: swiperContainer.querySelector(".gallery-swiper-button-prev")
              },
              pagination: {
                  el: swiperContainer.querySelector(".gallery-swiper-pagination"),
                  clickable: true,
              },
              breakpoints: {
                  320: {
                      slidesPerView: mobile,
                      spaceBetween: mobilespace
                  },
                  576: {
                      slidesPerView: tablet,
                      spaceBetween: mobilespace
                  },
                  768: {
                      slidesPerView: largeTablet,
                      spaceBetween: mobilespace
                  },
                  992: {
                      slidesPerView: desktop,
                      spaceBetween: desktopspace
                  },
              },
          });

          // Add hover functionality to stop and start autoplay
          sgBindAutoplayHover(swiperContainer, GallerySwiper);

          GallerySwipers.push(GallerySwiper);
      }
  }

  // Initialize Gallery Swiper on page load
  document.querySelectorAll('.gallery-slider').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initGallerySwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Gallery Swiper on section select
      GallerySwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      GallerySwipers = [];
      document.querySelectorAll('.gallery-slider').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initGallerySwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Gallery Swiper on section deselect if necessary
      GallerySwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      GallerySwipers = [];
      document.querySelectorAll('.gallery-slider').forEach(sgCancelLazyInit);
  });
});

// Gallery slider2 js

document.addEventListener('DOMContentLoaded', function () {
    var Gallery2Swipers = [];

    function initGallery2Swiper(swiperContainer) {
        var enable = swiperContainer.dataset.enable;
        var desktopspace = swiperContainer.dataset.desktopspace;
        var mobilespace = swiperContainer.dataset.mobilespace;
        var desktop = swiperContainer.dataset.desktop;
        var largeTablet = swiperContainer.dataset.largetablet;
        var tablet = swiperContainer.dataset.tablet;
        var mobile = swiperContainer.dataset.mobile;
        var autoplay = swiperContainer.dataset.autoplay === "true";
        var loop = swiperContainer.dataset.loop === "true";
        var speed = swiperContainer.dataset.speed;

        if (enable) {
            var Gallery2Swiper = new Swiper(swiperContainer, {
                slidesPerView: desktop,
                spaceBetween: desktopspace,
                loop: loop,
                autoplay: autoplay,
                speed: speed,
                grabCursor: true,
                a11y: false,
                navigation: {
                    nextEl: swiperContainer.querySelector(".gallery2-swiper-button-next"),
                    prevEl: swiperContainer.querySelector(".gallery2-swiper-button-prev")
                },
                pagination: {
                    el: swiperContainer.querySelector(".gallery2-swiper-pagination"),
                    clickable: true,
                },
                breakpoints: {
                    320: {
                        slidesPerView: mobile,
                        spaceBetween: mobilespace
                    },
                    576: {
                        slidesPerView: tablet,
                        spaceBetween: mobilespace
                    },
                    768: {
                        slidesPerView: largeTablet,
                        spaceBetween: mobilespace
                    },
                    992: {
                        slidesPerView: desktop,
                        spaceBetween: desktopspace
                    },
                },
            });

            // Add hover functionality to stop and start autoplay
            sgBindAutoplayHover(swiperContainer, Gallery2Swiper);

            Gallery2Swipers.push(Gallery2Swiper);
        }
    }

    // Initialize Gallery2 Swiper on page load
    document.querySelectorAll('.gallery2-slider').forEach(function (swiperContainer) {
        sgLazyInit(swiperContainer, initGallery2Swiper);
    });

    document.addEventListener('shopify:section:select', function () {
        // Reinitialize Gallery2 Swiper on section select
        Gallery2Swipers.forEach(function (swiper) {
            swiper.destroy();
        });
        Gallery2Swipers = [];
        document.querySelectorAll('.gallery2-slider').forEach(function (swiperContainer) {
            sgCancelLazyInit(swiperContainer);
            initGallery2Swiper(swiperContainer);
        });
    });

    document.addEventListener('shopify:section:deselect', function () {
        // Cleanup Gallery2 Swiper on section deselect if necessary
        Gallery2Swipers.forEach(function (swiper) {
            swiper.destroy();
        });
        Gallery2Swipers = [];
        document.querySelectorAll('.gallery2-slider').forEach(sgCancelLazyInit);
    });
});

// Automated image slider js

document.addEventListener('DOMContentLoaded', function () {
    var AutomatedSwipers = [];

    function initAutomatedSwiper(swiperContainer) {
        var enable = swiperContainer.dataset.enable;
        var desktopspace = swiperContainer.dataset.desktopspace;
        var mobilespace = swiperContainer.dataset.mobilespace;
        var desktop = swiperContainer.dataset.desktop;
        var largeTablet = swiperContainer.dataset.largetablet;
        var tablet = swiperContainer.dataset.tablet;
        var mobile = swiperContainer.dataset.mobile;
        var autoplay = swiperContainer.dataset.autoplay === "true";
        var loop = swiperContainer.dataset.loop === "true";
        var speed = swiperContainer.dataset.speed;

        if (enable) {
            var AutomatedSwiper = new Swiper(swiperContainer, {
                direction: "horizontal",
                slidesPerView: desktop,
                spaceBetween: desktopspace,
                loop: loop,
                autoplay: autoplay,
                speed: speed,
                grabCursor: true,
                a11y: false,
                parallax: true,
                centeredSlides: true,
                effect: "coverflow",
                coverflowEffect: {
                    rotate: 45,
                    slideShadows: true
                },
                navigation: {
                    nextEl: swiperContainer.querySelector(".automated-swiper-button-next"),
                    prevEl: swiperContainer.querySelector(".automated-swiper-button-prev")
                },
                pagination: {
                    el: swiperContainer.querySelector(".automated-swiper-pagination"),
                    clickable: true,
                },
                breakpoints: {
                    320: {
                        slidesPerView: mobile,
                        spaceBetween: mobilespace
                    },
                    576: {
                        slidesPerView: tablet,
                        spaceBetween: mobilespace
                    },
                    768: {
                        slidesPerView: largeTablet,
                        spaceBetween: mobilespace
                    },
                    992: {
                        slidesPerView: desktop,
                        spaceBetween: desktopspace
                    },
                },
            });

            // Add hover functionality to stop and start autoplay
            sgBindAutoplayHover(swiperContainer, AutomatedSwiper);

            AutomatedSwipers.push(AutomatedSwiper);
        }
    }

    // Initialize Automated Swiper on page load
    document.querySelectorAll('.automated-slider').forEach(function (swiperContainer) {
        sgLazyInit(swiperContainer, initAutomatedSwiper);
    });

    document.addEventListener('shopify:section:select', function () {
        // Reinitialize Automated Swiper on section select
        AutomatedSwipers.forEach(function (swiper) {
            swiper.destroy();
        });
        AutomatedSwipers = [];
        document.querySelectorAll('.automated-slider').forEach(function (swiperContainer) {
            sgCancelLazyInit(swiperContainer);
            initAutomatedSwiper(swiperContainer);
        });
    });

    document.addEventListener('shopify:section:deselect', function () {
        // Cleanup Automated Swiper on section deselect if necessary
        AutomatedSwipers.forEach(function (swiper) {
            swiper.destroy();
        });
        AutomatedSwipers = [];
        document.querySelectorAll('.automated-slider').forEach(sgCancelLazyInit);
    });
});

// Automated image slider2 js

document.addEventListener('DOMContentLoaded', function () {
    var AutomatedSwipers = [];

    function initAutomatedSwiper(swiperContainer) {
        var enable = swiperContainer.dataset.enable;
        var desktopspace = parseInt(swiperContainer.dataset.desktopspace);
        var mobilespace = parseInt(swiperContainer.dataset.mobilespace);
        var desktop = parseInt(swiperContainer.dataset.desktop);
        var largeTablet = parseInt(swiperContainer.dataset.largetablet);
        var tablet = parseInt(swiperContainer.dataset.tablet);
        var mobile = parseInt(swiperContainer.dataset.mobile);
        var autoplay = swiperContainer.dataset.autoplay === "true";
        var loop = swiperContainer.dataset.loop === "true";
        var speed = parseInt(swiperContainer.dataset.speed);

        if (enable) {
            var AutomatedSwiper = new Swiper(swiperContainer, {
                direction: "horizontal",
                slidesPerView: desktop,
                spaceBetween: desktopspace,
                loop: loop,
                autoplay: autoplay,
                speed: speed,
                grabCursor: true,
                a11y: false,
                parallax: true,
                centeredSlides: true,
                loopAdditionalSlides: 2,
                effect: "coverflow",
                coverflowEffect: {
                    rotate: 0,
                    stretch: 50,
                    depth: 100,
                    modifier: 4,
                    slideShadows: true
                },
                navigation: {
                    nextEl: swiperContainer.querySelector(".automated2-swiper-button-next"),
                    prevEl: swiperContainer.querySelector(".automated2-swiper-button-prev")
                },
                pagination: {
                    el: swiperContainer.querySelector(".automated2-swiper-pagination"),
                    clickable: true,
                },
                breakpoints: {
                    320: {
                        slidesPerView: mobile,
                        spaceBetween: mobilespace,
                    },
                    576: {
                        slidesPerView: tablet,
                        spaceBetween: mobilespace
                    },
                    768: {
                        slidesPerView: largeTablet,
                        spaceBetween: mobilespace
                    },
                    992: {
                        slidesPerView: desktop,
                        spaceBetween: desktopspace
                    },
                },
                on: {
                    init: function () {
                        this.slideToLoop(1, 0); // Correctly reference the current Swiper instance
                    },
                }
            });

            // Add hover functionality to stop and start autoplay
            sgBindAutoplayHover(swiperContainer, AutomatedSwiper);

            AutomatedSwipers.push(AutomatedSwiper);
        }
    }

    // Initialize Automated Swiper on page load
    document.querySelectorAll('.automated2-slider').forEach(function (swiperContainer) {
        sgLazyInit(swiperContainer, initAutomatedSwiper);
    });

    document.addEventListener('shopify:section:select', function () {
        // Reinitialize Automated Swiper on section select
        AutomatedSwipers.forEach(function (swiper) {
            swiper.destroy(); // Clean up existing Swipers
        });
        AutomatedSwipers = [];
        document.querySelectorAll('.automated2-slider').forEach(function (swiperContainer) {
            sgCancelLazyInit(swiperContainer);
            initAutomatedSwiper(swiperContainer); // Reinitialize
        });
    });

    document.addEventListener('shopify:section:deselect', function () {
        // Cleanup Automated Swiper on section deselect if necessary
        AutomatedSwipers.forEach(function (swiper) {
            swiper.destroy();
        });
        AutomatedSwipers = [];
        document.querySelectorAll('.automated2-slider').forEach(sgCancelLazyInit);
    });
});

// recentview collection slider js

document.addEventListener('DOMContentLoaded', function () {
    var recentviewcollectionSwipers = [];

    function initrecentviewcollectionSwiper(swiperContainer) {
        var desktopspace = swiperContainer.dataset.desktopspace;
        var mobilespace = swiperContainer.dataset.mobilespace;
        var desktop = swiperContainer.dataset.desktop;
        var largeTablet = swiperContainer.dataset.largetablet;
        var tablet = swiperContainer.dataset.tablet;
        var mobile = swiperContainer.dataset.mobile;
        var autoplay = swiperContainer.dataset.autoplay === "true";
        var loop = swiperContainer.dataset.loop === "true";
        var speed = swiperContainer.dataset.speed;

        // Initialize the Swiper instance
        var recentviewcollectionSwiper = new Swiper(swiperContainer, {
            slidesPerView: desktop,
            spaceBetween: desktopspace,
            loop: loop,
            autoplay: autoplay,
            speed: speed,
            grabCursor: true,
            a11y: false,
            navigation: {
                nextEl: swiperContainer.querySelector(".recent-swiper-button-next"),
                prevEl: swiperContainer.querySelector(".recent-swiper-button-prev")
            },
            pagination: {
                el: swiperContainer.querySelector(".recent-swiper-pagination"),
                clickable: true,
            },
            breakpoints: {
                320: {
                    slidesPerView: mobile,
                    spaceBetween: mobilespace
                },
                576: {
                    slidesPerView: tablet,
                    spaceBetween: mobilespace
                },
                768: {
                    slidesPerView: largeTablet,
                    spaceBetween: mobilespace
                },
                992: {
                    slidesPerView: desktop,
                    spaceBetween: desktopspace
                },
            },
        });

        // Add hover functionality to pause and resume autoplay
        sgBindAutoplayHover(swiperContainer, recentviewcollectionSwiper);

        recentviewcollectionSwipers.push(recentviewcollectionSwiper);
    }

    // Initialize recent view collection Swiper on page load
    document.querySelectorAll('.recent-slider').forEach(function (swiperContainer) {
        sgLazyInit(swiperContainer, initrecentviewcollectionSwiper);
    });

    document.addEventListener('shopify:section:select', function () {
        // Reinitialize recent view collection Swiper on section select
        recentviewcollectionSwipers.forEach(function (swiper) {
            swiper.destroy();
        });
        recentviewcollectionSwipers = [];
        document.querySelectorAll('.recent-slider').forEach(function (swiperContainer) {
            sgCancelLazyInit(swiperContainer);
            initrecentviewcollectionSwiper(swiperContainer);
        });
    });

    document.addEventListener('shopify:section:deselect', function () {
        // Cleanup recent view collection Swiper on section deselect if necessary
        recentviewcollectionSwipers.forEach(function (swiper) {
            swiper.destroy();
        });
        recentviewcollectionSwipers = [];
        document.querySelectorAll('.recent-slider').forEach(sgCancelLazyInit);
    });
});

document.addEventListener('DOMContentLoaded', function () {
    var StorelocatorSwipers = [];

    function initStorelocatorSwiper(swiperContainer) {
        var desktopspace = parseInt(swiperContainer.dataset.desktopspace, 10);
        var mobilespace = parseInt(swiperContainer.dataset.mobilespace, 10);
        var desktop = parseInt(swiperContainer.dataset.desktop, 10);
        var largeTablet = parseInt(swiperContainer.dataset.largetablet, 10);
        var tablet = parseInt(swiperContainer.dataset.tablet, 10);
        var mobile = parseInt(swiperContainer.dataset.mobile, 10);
        var autoplay = swiperContainer.dataset.autoplay === "true";
        var loop = swiperContainer.dataset.loop === "true";
        var speed = parseInt(swiperContainer.dataset.speed, 10);

        // Initialize the Swiper only if enabled
        {
            var StorelocatorSwiper = new Swiper(swiperContainer, {
                slidesPerView: desktop,
                spaceBetween: desktopspace,
                loop: loop,
                autoplay: autoplay,
                speed: speed,
                grabCursor: true,
                a11y: false,
                navigation: {
                    nextEl: swiperContainer.querySelector(".locator-swiper-button-next"),
                    prevEl: swiperContainer.querySelector(".locator-swiper-button-prev")
                },
                pagination: {
                    el: swiperContainer.querySelector(".locator-swiper-pagination"),
                    clickable: true,
                },
                breakpoints: {
                    320: {
                        slidesPerView: mobile,
                        spaceBetween: mobilespace
                    },
                    576: {
                        slidesPerView: tablet,
                        spaceBetween: mobilespace
                    },
                    768: {
                        slidesPerView: largeTablet,
                        spaceBetween: mobilespace
                    },
                    992: {
                        slidesPerView: largeTablet,
                        spaceBetween: desktopspace
                    },
                    1200: {
                        slidesPerView: desktop,
                        spaceBetween: desktopspace
                    },
                },
            });

            // Hover functionality to pause and resume autoplay
            sgBindAutoplayHover(swiperContainer, StorelocatorSwiper);

            StorelocatorSwipers.push(StorelocatorSwiper);
        }
    }

    // Initialize Store locator Swiper on page load
    document.querySelectorAll('.locator-slider').forEach(function (swiperContainer) {
        sgLazyInit(swiperContainer, initStorelocatorSwiper);
    });

    document.addEventListener('shopify:section:select', function () {
        // Reinitialize Store locator Swiper on section select
        StorelocatorSwipers.forEach(function (swiper) {
            swiper.destroy();
        });
        StorelocatorSwipers = [];
        document.querySelectorAll('.locator-slider').forEach(function (swiperContainer) {
            sgCancelLazyInit(swiperContainer);
            initStorelocatorSwiper(swiperContainer);
        });
    });

    document.addEventListener('shopify:section:deselect', function () {
        // Cleanup Store locator Swiper on section deselect if necessary
        StorelocatorSwipers.forEach(function (swiper) {
            swiper.destroy();
        });
        StorelocatorSwipers = [];
        document.querySelectorAll('.locator-slider').forEach(sgCancelLazyInit);
    });
});

document.addEventListener('DOMContentLoaded', function () {
  var BundleSwipers = [];

  function initBundleSwiper(swiperContainer) {
  
      var BundleSwiper = new Swiper(swiperContainer, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          autoplay: false,
          speed: 500,
          grabCursor: true,
          a11y: false,
          navigation: {
              nextEl: swiperContainer.querySelector(".bundle-swiper-button-next"),
              prevEl: swiperContainer.querySelector(".bundle-swiper-button-prev")
          },
          pagination: {
              el: swiperContainer.querySelector(".bundle-swiper-pagination"),
              clickable: true,
          },
          breakpoints: {
            320: {
                slidesPerView: 1,
                spaceBetween: 0
            }
        },
      });

    BundleSwipers.push(BundleSwiper);
  }

  // Initialize Featured blog Swiper on page load
  document.querySelectorAll('.card-bundle').forEach(function (swiperContainer) {
      sgLazyInit(swiperContainer, initBundleSwiper);
  });

  document.addEventListener('shopify:section:select', function () {
      // Reinitialize Featured blog Swiper on section select
      BundleSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      BundleSwipers = [];
      document.querySelectorAll('.card-bundle').forEach(function (swiperContainer) {
          sgCancelLazyInit(swiperContainer);
          initBundleSwiper(swiperContainer);
      });
  });

  document.addEventListener('shopify:section:deselect', function () {
      // Cleanup Featured blog Swiper on section deselect if necessary
      BundleSwipers.forEach(function (swiper) {
          swiper.destroy();
      });
      BundleSwipers = [];
      document.querySelectorAll('.card-bundle').forEach(sgCancelLazyInit);
  });
});

// Registered last, so it runs after every slider type above has registered its containers
document.addEventListener('DOMContentLoaded', function () {
  if (sgHasIO) sgSettle();
});
})();
